import React, { useEffect, useRef, useState } from 'react'
import {
  format,
  startOfWeek,
  addDays,
  startOfMonth,
  endOfMonth,
  endOfWeek,
  isSameMonth,
  addMonths,
  subMonths,
  isSameDay,
  getMonth,
  getYear,
  isWithinInterval,
  addYears,
  subYears,
  setMonth,
  differenceInCalendarDays,
} from 'date-fns'
import { useSelector } from 'react-redux'
import _ from 'lodash'
import PropTypes from 'prop-types'

import rightCalendarIcon from '../../assets/icons/right-calender.svg'
import leftCalendarIcon from '../../assets/icons/cale-left.svg'
import {
  getBrand,
  getPricingComment,
} from '../../redux/reducers/configuration'
import {
  getClosestAvailableDepartureDate,
  getTourItinerary,
} from '../../redux/reducers/tourDetails'
import { onEnter } from '../../utilities/accessibility/onEnter'
import { addBrandCurrencyPrefixSuffix } from '../../utilities/currency/formatPrice'

import '../../styles/scss/elements/calendar.scss'

const CALL_FOR_PRICING = 'Call for pricing'
const CLOSED_STATUS = 'C'
const SOLD_OUT_STATUS = 'S'

const Calendar = ({
  onCalendarCallBack,
  onSelectPricingPlan,
  selectedDate,
  submitButton,
  tourStartDate,
}) => {
  const popoverRef = useRef(null)
  const brandCode = useSelector(getBrand)
  const calendarPricingComment = useSelector(getPricingComment)
  const tourItineraryAndPricing = useSelector(getTourItinerary)
  const closestAvailableDepartureDate = useSelector(
    getClosestAvailableDepartureDate
  )
  const [currentDate, setCurrentDate] = useState(
    selectedDate
      ? new Date(selectedDate)
      : closestAvailableDepartureDate
        ? closestAvailableDepartureDate
        : new Date()
  )
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [selectedPricingCategory, setSelectedPricingCategory] = useState(0)

  const tourDuration = _.get(tourItineraryAndPricing, 'duration')
  const tourPricing = _.get(tourItineraryAndPricing, 'availability')

  useEffect(() => {
    onSelectPricingPlan(_.get(tourPricing, '[0].id'))
  }, [onSelectPricingPlan, tourPricing])

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, false)
    document.addEventListener('keydown', onEsc, false)
    return () => {
      document.removeEventListener('click', handleClickOutside, false)
      document.removeEventListener('keydown', onEsc, false)
    }
  }, [])

  const handleClickOutside = (event) => {
    if (popoverRef.current && !popoverRef.current.contains(event.target)) {
      setIsPopoverOpen(false)
    }
  }

  const onEsc = (event) => {
    if (event.keyCode === 27) {
      setIsPopoverOpen(false)
    }
  }

  function onChangePricingCategory(index) {
    setSelectedPricingCategory(index)
    onSelectPricingPlan(_.get(tourPricing, `[${index}].id`))
    // clear date if selected
    onCalendarCallBack('', '')
  }

  const nextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1))
  }

  const prevMonth = () => {
    const currentMonth = getMonth(new Date())
    const requestedMonth = getMonth(subMonths(currentDate, 1))

    if (getYear(subMonths(currentDate, 1)) < getYear(new Date())) {
      return
    }

    if (getYear(new Date()) === getYear(currentDate)) {
      if (requestedMonth < currentMonth) {
        return
      }
    }
    setCurrentDate(subMonths(currentDate, 1))
  }

  const nextYear = () => {
    setCurrentDate(addYears(currentDate, 1))
  }

  const prevYear = () => {
    const currentYear = getYear(new Date())
    const requestedYear = getYear(subYears(currentDate, 1))
    const currentMonth = getMonth(new Date())
    const requestedMonth = getMonth(subMonths(currentDate, 1))
    if (requestedYear < currentYear) {
      return
    }

    if (requestedYear === currentYear) {
      if (requestedMonth < currentMonth) {
        setCurrentDate(new Date())
        return
      }
    }

    setCurrentDate(subYears(currentDate, 1))
  }

  const toMonth = (month) => {
    const stateCurrentyear = getYear(currentDate)
    const currentYear = getYear(new Date())
    if (stateCurrentyear === currentYear) {
      const currentMonth = getMonth(new Date())
      if (month < currentMonth) {
        return
      }
    }
    setCurrentDate(setMonth(currentDate, month))
  }

  const onDateClick = (day) => {
    onCalendarCallBack(day, addDays(day, tourDuration - 1))
  }

  const todayDate = () => {
    setCurrentDate(new Date())
  }

  const isDateActive = (date) => {
    const formattedDate = format(date, 'yyyy-MM-dd')
    const tripDate = _.get(
      tourPricing,
      `[${selectedPricingCategory}].pricing[${formattedDate}]`
    )

    const price = _.get(tripDate, 'amount')
    const isClosed = _.get(tripDate, 'saleStatus') === CLOSED_STATUS
    const isSoldOut = _.get(tripDate, 'saleStatus') === SOLD_OUT_STATUS

    return price && !isClosed && !isSoldOut
  }



  const getCalendarCellLabel = (date) => {
    const formattedDate = format(date, 'yyyy-MM-dd')
    const tripDate = _.get(
      tourPricing,
      `[${selectedPricingCategory}].pricing[${formattedDate}]`
    )

    const price = _.get(tripDate, 'amount')
    const isClosed = _.get(tripDate, 'saleStatus') === CLOSED_STATUS
    const isSoldOut = _.get(tripDate, 'saleStatus') === SOLD_OUT_STATUS

    if (differenceInCalendarDays(date, new Date()) < 0) {
      return ''
    } else if (!_.isNumber(price) || isClosed) {
      return CALL_FOR_PRICING
    } else if (isSoldOut) {
      return 'Sold Out'
    }
    return price !== 0
      ? addBrandCurrencyPrefixSuffix(brandCode, price.toLocaleString())
      : CALL_FOR_PRICING
  }

  const addHighlightedBlue = (date) => {
    if (tourStartDate) {
      return isWithinInterval(date, {
        start: addDays(tourStartDate, 1),
        end: addDays(tourStartDate, tourDuration - 1),
      })
    }
  }

  const calendarControls = () => {
    const yearFormat = 'yyyy'
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ]
    return (
      <div
        className="right-calender-icon__button"
        onClick={() => setIsPopoverOpen(true)}
        onKeyDown={(event) => onEnter(event, () => setIsPopoverOpen(true))}
        tabIndex="0"
        role="button"
        ref={popoverRef}
      >
        <span>Jump to date</span>
        {isPopoverOpen && (
          <div className="u-flex-column right-calender-icon__button__popover">
            <div className="right-calender-icon__button__popover__wrapper">
              <div className="u-flex right-calender-icon__button__popover__year-wrapper">
                <div className="column col-start" aria-label="previous year">
                  <div
                    className="calendar-header-icon"
                    onClick={prevYear}
                    onKeyDown={(event) => onEnter(event, prevYear)}
                    tabIndex="0"
                    style={{ float: 'right' }}
                  >
                    <img src={leftCalendarIcon} alt="left calendar icon" />
                  </div>
                </div>
                <div
                  className="column col-center year"
                  aria-label="current year"
                >
                  {format(currentDate, yearFormat)}
                </div>
                <div
                  className="u-flex column col-end right-calender-icon"
                  aria-label="next year"
                >
                  <div
                    className="calendar-header-icon"
                    onClick={nextYear}
                    onKeyDown={(event) => onEnter(event, nextYear)}
                    tabIndex="0"
                  >
                    <img src={rightCalendarIcon} alt="right calendar icon" />
                  </div>
                </div>
              </div>
              <div className="right-calender-icon__button__popover__month-wrapper">
                {monthNames.map((month, i) => {
                  return (
                    <div
                      className={`u-flex u-border right-calender-icon__button__popover__month-name ${
                        getMonth(currentDate) === i ? 'sel' : ''
                      }`}
                      key={i}
                      onClick={() => toMonth(i)}
                      onKeyDown={(event) => onEnter(event, () => toMonth(i))}
                      tabIndex="0"
                      role="button"
                      aria-label="month"
                    >
                      {month}
                    </div>
                  )
                })}
              </div>
              <div
                className="right-calender-icon__button__popover__footer"
                role="button"
                aria-label="Today"
                onClick={() => {
                  todayDate()
                }}
                onKeyDown={(event) => onEnter(event, () => todayDate())}
                tabIndex="0"
              >
                Today
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  const header = () => {
    const dateFormat = 'MMMM'
    const yearFormat = 'yyyy'
    return (
      <div>
        {tourPricing.length > 1 && (
          <div className="pricing-categories">
            {_.map(tourPricing, (category, index) => (
              <button
                key={`${index}`}
                className={
                  index === selectedPricingCategory
                    ? 'pricing-category-selected'
                    : ''
                }
                onClick={() => onChangePricingCategory(index)}
                onKeyDown={(event) =>
                  onEnter(event, () => onChangePricingCategory(index))
                }
              >
                {category.title}
              </button>
            ))}
          </div>
        )}
        <div className="row row-middle u-flex-justify-center">
          <div className="u-flex month-navigator">
            {calendarControls()}
            <div className="column col-start" aria-label="previous month">
              <div
                className="calendar-header-icon"
                onClick={prevMonth}
                style={{ float: 'right' }}
                onKeyDown={(event) => onEnter(event, prevMonth)}
                tabIndex="0"
              >
                <img src={leftCalendarIcon} alt="left calendar icon" />
              </div>
            </div>
            <div className="column col-center" aria-label="current month">
              <span className="month-name">
                {format(currentDate, dateFormat)}{' '}
                {format(currentDate, yearFormat)}
              </span>
            </div>
            <div
              className="u-flex column col-end right-calender-icon"
              aria-label="next month"
            >
              <div
                className="calendar-header-icon"
                onClick={nextMonth}
                onKeyDown={(event) => onEnter(event, nextMonth)}
                tabIndex="0"
              >
                <img src={rightCalendarIcon} alt="right calendar icon" />
              </div>
            </div>
          </div>
          {submitButton}
        </div>
      </div>
    )
  }
  const days = () => {
    const dateFormat = 'EEEE'
    const days = []
    let startDate = startOfWeek(currentDate)
    for (let i = 0; i < 7; i++) {
      days.push(
        <div className="column col-center" key={i}>
          {format(addDays(startDate, i), dateFormat)}
        </div>
      )
    }
    return (
      <div className="days row" aria-label="days">
        {days}
      </div>
    )
  }

  const cells = () => {
    const monthStart = startOfMonth(currentDate)
    const monthEnd = endOfMonth(monthStart)
    const startDate = startOfWeek(monthStart)
    const endDate = endOfWeek(monthEnd)
    const dateFormat = 'd'
    const rows = []
    let days = []
    let day = startDate
    let formattedDate = ''
    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        formattedDate = format(day, dateFormat)
        const cloneDay = day
        const isActive = isDateActive(cloneDay)
        const calendarCellLabel = getCalendarCellLabel(cloneDay);
        days.push(
          <div
            className={`column cell ${
              !isSameMonth(day, monthStart)
                ? 'disabled'
                : tourStartDate && isSameDay(day, tourStartDate)
                ? 'sel'
                : ''
            } ${isActive ? '' : 'disabled'} 
            ${addHighlightedBlue(day) ? 'highlight-blue' : ''}`}
            key={day}
            onClick={() => onDateClick(cloneDay)}
            onKeyDown={(event) => onEnter(event, () => onDateClick(cloneDay))}
            aria-label="days cell"
            tabIndex={
              isSameMonth(cloneDay, monthStart) && isActive ? '0' : null
            }
          >
            <span className="number">{formattedDate}</span>
            <div className='price-wrapper'>
              {calendarCellLabel === CALL_FOR_PRICING && (
                 <span className="call-for-pricing">
                  {CALL_FOR_PRICING}
                </span>
              )}
              {calendarCellLabel !== CALL_FOR_PRICING && (
                <>
                  {calendarCellLabel && 
                    calendarCellLabel !== "Sold Out" && (
                      <span className='price-label'>From</span>
                    )}
                  <span className="price">{getCalendarCellLabel(cloneDay)}</span>
                </>
              )}
            </div>
          </div>
        )
        day = addDays(day, 1)
      }
      rows.push(
        <div className="row" key={day}>
          {' '}
          {days}{' '}
        </div>
      )
      days = []
    }
    return <div className="body">{rows}</div>
  }
  return (
    <div className="calendar">
      <div className="calendar__header">{header()}</div>
      <div aria-label="days">{days()}</div>
      <div aria-label="days-cell">{cells()}</div>
      <p className="pricing-disclaimer">
        * {calendarPricingComment}
      </p>
    </div>
  )
}

Calendar.propTypes = {
  onCalendarCallBack: PropTypes.func.isRequired,
  onSelectPricingPlan: PropTypes.func.isRequired,
}

export default Calendar
